import type { ReactNode } from "react";
import { FieldLabel } from "@/components/ds/EditableField";

/**
 * Un champ : son libellé, le champ, puis l'aide et l'erreur.
 *
 * L'aide est SOUS le champ, et c'est une correction. Placée entre le libellé et le
 * champ, elle décalait le champ vers le bas — et dans une grille à deux colonnes,
 * un champ avec aide ne s'alignait plus avec son voisin sans aide. La ligne
 * « Nom de l'entreprise / SIRET » était visiblement bancale.
 *
 * Sous le champ, le bloc libellé + champ fait toujours la même hauteur, quelle que
 * soit la présence d'une aide, et les colonnes s'alignent d'elles-mêmes.
 *
 * Le libellé est `FieldLabel`, celui de tous les autres champs (14/20, secondaire,
 * étoile quand il est obligatoire) : il était ici en 13/600 couleur d'encre, et le
 * même formulaire se lisait autrement selon l'écran. Aide en `caption` (12/16),
 * erreur en `caption-strong` (12/16/600).
 */
export function FormField({
	id,
	label,
	required = false,
	hint,
	error,
	wide = false,
	children,
}: {
	id: string;
	label: string;
	required?: boolean;
	hint?: string;
	error?: string;
	wide?: boolean;
	children: ReactNode;
}) {
	return (
		<div className={wide ? "sm:col-span-2" : ""}>
			<FieldLabel htmlFor={id} label={label} required={required} />
			{children}
			{error ? (
				<p id={`${id}-aide`} role="alert" className="t-caption-strong mt-1 text-mg-error-fg">
					{error}
				</p>
			) : hint ? (
				<p id={`${id}-aide`} className="t-caption mt-1 text-mg-secondary">
					{hint}
				</p>
			) : null}
		</div>
	);
}

/**
 * Un titre de section, et rien d'autre.
 *
 * La pastille numérotée a sauté : deux sections n'ont pas besoin d'être comptées,
 * et la pastille était fausse — son fond prenait une teinte fixe pendant que le
 * chiffre prenait la couleur de l'école, ce qui donnait de l'orange sur du bleu
 * pâle chez ACE. Un trait fin sépare mieux deux groupes qu'un rond coloré.
 */
export function FormSection({ title, children }: { title: string; children: ReactNode }) {
	return (
		<section>
			<h2 className="t-h4 border-b border-mg-border-subtle pb-3 text-mg-heading">{title}</h2>
			<div className="mt-5 grid gap-x-5 gap-y-4 sm:grid-cols-2">{children}</div>
		</section>
	);
}
